/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any */

import { wrap } from 'comlink';
import { DateTime } from 'luxon';

import { STATUS } from '@/constants';
import { container } from '@/container';
import CONSTANTS from '@/helpers/constants';
import { Logger } from '@/services/Logger';
import { ReturnTripUpdateReq } from '@/services/signalr/MessageTypes/TripUpdateReq';
import { Signalr } from '@/services/signalr/Signalr';
import { plainObject } from '@/utils/plainObject';
import { swapLegs } from '@/utils/swapLegs';

const logger = container.get(Logger);

/**
 * Load the web worker for message construction.
 * @type {{
 *   createTripMessage: (function(trip: object, options?: {hasPatientFields: boolean, hasPatientEdit: boolean, hasCallSources: boolean}): Promise<object>),
 * }}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const worker: any = wrap(new Worker(new URL('../../store/events/updateTrip.worker.js', import.meta.url), { type: 'module' }));

export interface CreateReturnTripParams {
  trip: Record<string, unknown>;
}

/**
 * Create a return trip (for trips with return leg)
 */
export async function createReturnTrip(params: CreateReturnTripParams): Promise<void> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { trip } = params;
    const tripData = {
      ...trip,
      appointmentTime: undefined,
      requestedTime: undefined,
      tripType: CONSTANTS.TRIPS.ADD_TRIP_OPTIONS.RETURN_TRIP,
      tripStatus: '',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      pickupTime: DateTime.fromISO(trip.returnPickupTime as string).toFormat(CONSTANTS.FORMATS.TIME),
      unit: '',
      unitId: '',
      id: '',
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      pickupWillCall: trip.returnWillCall,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      copiedFromTripId: trip.id,
      history: null,
      callStartedDateTime: DateTime.now().toUTC().toISO(),
      pickupAddress1: null,
      pickupAddress2: null,
      pickupPhoneNumber: null,
      dropoffAddress1: null,
      dropoffAddress2: null,
      dropoffPhoneNumber: null,
    };
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const updatedTrip = swapLegs(tripData as any);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (trip?.statusTimes) {
      const statusTimes = [];
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any
      const createdStatusTime = (trip.statusTimes as any[]).find((statusTime: any) => statusTime.status.id === '0');
      const statusTime = {
        status: {
          id: '0',
          value: '',
        },
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        timestamp: createdStatusTime?.timestamp ?? '',
        enabled: STATUS.ENABLED,
        overdue: false,
      };

      statusTimes.push(statusTime);
      updatedTrip.statusTimes = statusTimes;
    }

    const signalr = await container.getAsync(Signalr);
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
    await signalr.invoke(ReturnTripUpdateReq, {
      contextKey: CONSTANTS.UI.CONTEXTS.CALL_TAKING,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      ...await worker.createTripMessage(plainObject(updatedTrip)),
    }, { suppressSuccessToast: true });
  }
  catch (error) {
    logger.captureException(error as Error, { tags: { createTrip: 'createReturnTrip' } });
    throw error;
  }
}
